import {
  BOLD_REGEX,
  checkItalicChar,
  findNextTokenIndex,
  INLINE_CODE_REGEX,
  type MarkdownToken,
} from "@/components/markdown/utils";

export const parseMarkdownCell = (text: string): React.ReactNode => {
  if (!text) return text;

  const tokens: MarkdownToken[] = [];
  let currentIndex = 0;
  let keyCounter = 0;

  while (currentIndex < text.length) {
    const remainingText = text.slice(currentIndex);

    const codeMatch = remainingText.match(INLINE_CODE_REGEX);
    if (codeMatch) {
      tokens.push({ type: "code", content: codeMatch[1] || "" });
      currentIndex += codeMatch[0].length;
      continue;
    }

    const boldMatch = remainingText.match(BOLD_REGEX);
    if (boldMatch) {
      tokens.push({ type: "bold", content: boldMatch[2] || "" });
      currentIndex += boldMatch[0].length;
      continue;
    }

    const italicAsterisk = checkItalicChar(text, currentIndex, "*");
    const italicUnderscore = checkItalicChar(text, currentIndex, "_");
    const italicMatch = italicAsterisk || italicUnderscore;

    if (italicMatch) {
      tokens.push({ type: "italic", content: italicMatch.content });
      currentIndex += italicMatch.length;
      continue;
    }

    const nextTokenIndex = findNextTokenIndex(text, currentIndex);
    const textContent = text.slice(currentIndex, nextTokenIndex);
    if (textContent) {
      tokens.push({ type: "text", content: textContent });
    }
    currentIndex = nextTokenIndex;
  }

  const elements = tokens.map((token) => {
    keyCounter++;
    switch (token.type) {
      case "code":
        return (
          <code
            className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm"
            key={`cell-code-${keyCounter}`}
          >
            {token.content}
          </code>
        );
      case "bold":
        return (
          <strong className="font-medium" key={`cell-bold-${keyCounter}`}>
            {token.content}
          </strong>
        );
      case "italic":
        return <em key={`cell-italic-${keyCounter}`}>{token.content}</em>;
      case "text":
        return token.content;
      default:
        return (token as MarkdownToken).content;
    }
  });

  return elements.length === 1 ? elements[0] : elements;
};
